import { HttpEvent } from '@angular/common/http';
import { Division, VehicleListItem } from '@app/core/models/zonar-accounts.model';
import {
  ApiDriverAsset,
  ApiDriverAssetResponse,
  DriverAsset,
  DriverProfle
} from '@app/modules/driver/models/driver.models';
import { Asset } from '@app/modules/location-client/location-api.models';
import { ViewableAsset } from '@app/modules/location/models/viewable-asset.model';
import { ReverseGeocoderData } from '@app/modules/reverse-geocoder/services/models/reverse-geocoder-response.model';
import { Identifyable } from '@app/modules/shared/models/shared.models';
import { EnvironmentService } from '@app/services/environment.service';
import { LatLng } from 'leaflet';
import * as moment from 'moment-timezone';
import 'moment-duration-format';
import { Settings } from '@app/store/settings/reducers/settings.reducer';

export const MILES_PER_METER = 0.000621371;
export const KM_PER_METER = 0.001;
export const MPH_PER_MM_SEC = 0.0022;
export const KPH_PER_MM_SEC = 0.0036;
export const MILES_PER_KM_H = 0.621371;
// Regions:
export const EUROPE = 'EU';
// Zoom Scales
export const ZoomScalesInMeters = {
  5000000: 2,
  2000000: 3,
  1000000: 4,
  500000: 5,
  300000: 6,
  100000: 7,
  50000: 8,
  30000: 9,
  20000: 10,
  10000: 11,
  5000: 12,
  2000: 13,
  1000: 14,
  500: 15,
  300: 16,
  100: 17,
  50: 18,
  30: 19
};

export const round = (value: number, precision: number = 0): number => {
  const multiplier = Math.pow(10, precision || 0);
  return Math.round(value * multiplier) / multiplier;
};

export const mmPerSecToMph = (speed): number => {
  // convert asset speed in mm/sec to mph rounded to nearest whole value
  return Math.round(speed * MPH_PER_MM_SEC);
};

export const mmPerSecToKph = (speed): number => {
  return Math.round(speed * KPH_PER_MM_SEC);
};

export const metersToMiles = (meters: number): number => {
  return meters * MILES_PER_METER;
};

export const metersToKm = (meters: number): number => {
  return meters * KM_PER_METER;
};

export const processDivisionsFromCompanyApi = (divisions: Division[]): Division[] => {
  return divisions.filter(d => d.type.startsWith('LEGACY') && d.name && d.name.length);
};

export const getUniqueValues = (collection: object[], key: string) => {
  const saved = {};
  return collection.reduce((acc, cur) => {
    if (!saved[cur[key]]) {
      saved[cur[key]] = cur;
      (acc as Array<any>).push(cur);
    }
    return acc;
  }, []);
};

export const getMinutesSinceTimeStamp = (timestamp: string): number => {
  if (!timestamp) {
    return 0;
  }

  const now = moment();
  const eventTs = moment(timestamp);
  const minutes = now.diff(eventTs, 'minutes');

  return minutes >= 0 ? minutes : 0;
};

export const getDistance = (from: number[], to: number[]): number => {
  // return distance in meters
  const toRadian = degree => (degree * Math.PI) / 180;
  const lon1 = toRadian(from[1]);
  const lat1 = toRadian(from[0]);
  const lon2 = toRadian(to[1]);
  const lat2 = toRadian(to[0]);

  const deltaLat = lat2 - lat1;
  const deltaLon = lon2 - lon1;

  const a = Math.pow(Math.sin(deltaLat / 2), 2) + Math.cos(lat1) * Math.cos(lat2) * Math.pow(Math.sin(deltaLon / 2), 2);
  const c = 2 * Math.asin(Math.sqrt(a));
  const EARTH_RADIUS = 6371;
  return c * EARTH_RADIUS * 1000;
};

export const validGpsData = asset => {
  return asset ? Boolean(asset.geoEventTs && asset.latitude && asset.longitude) : false;
};

// asset display text utilities
export const getStateZipText = (geocodeData: ReverseGeocoderData) => {
  return `${geocodeData.district || ''} ${geocodeData.zipcode || ''}`;
};

export const getCityStateZipText = (geocodeData: ReverseGeocoderData) => {
  return geocodeData.locality
    ? `${geocodeData.locality || ''}, ${geocodeData.district || ''} ${geocodeData.zipcode || ''}`
    : getStateZipText(geocodeData);
};

export const getAddressCityStateZipText = (geocodeData: ReverseGeocoderData) => {
  return geocodeData.locality
    ? `${geocodeData.address || ''}, ${geocodeData.locality || ''}, ${geocodeData.district || ''} ${
        geocodeData.zipcode || ''
      }`
    : getStateZipText(geocodeData);
};

export const timeTextFormatNa = 'HH:mm';

export const formatTimestamp = (timeStamp: string, timeTextFormat = timeTextFormatNa, timeZone: string): string => {
  const datetime = moment.utc(timeStamp);
  //return datetime.isValid() ? datetime.local().format(timeTextFormat) : '';
  return datetime.isValid() ? datetime.tz(timeZone).format(timeTextFormat) : '';
};

export const dateTimeTextFormatNa = 'MM/DD/YYYY HH:mm:ss';

export const getDateTimeText = (
  timeStamp: string,
  dateTimeTextFormat = dateTimeTextFormatNa,
  timeZone: string
): string => {
  const datetime = moment.utc(timeStamp);
  //return datetime.isValid() ? datetime.local().format(dateTimeTextFormat) : '';
  return datetime.isValid() ? datetime.tz(timeZone).format(dateTimeTextFormat) : '';
};

export const getStateOfChargeText = (stateOfCharge: number): string => {
  return stateOfCharge > 0 ? `${Math.round(stateOfCharge * 10) / 10}%` : '';
};

export const interpolateTime = (ts1, ts2, fractionElapsed) => {
  const utc1 = moment.utc(ts1);
  const utc2 = moment.utc(ts2);
  const diff = utc2.diff(utc1);
  return utc1.add(diff * fractionElapsed, 'milliseconds').toISOString();
};

// this method is a copy/pasta from leaflet.geometryutil package, since our application is unable to import
// the geometryutil package due to a known issue.
// geometryutil package import issue: https://github.com/makinacorpus/Leaflet.GeometryUtil/blob/master/src/leaflet.geometryutil.js#L689
// source for this method: https://github.com/makinacorpus/Leaflet.GeometryUtil/blob/master/src/leaflet.geometryutil.js#L689
export const getHeading = (latLng1: LatLng, latLng2: LatLng): number => {
  const rad = Math.PI / 180;
  const lat1 = latLng1.lat * rad;
  const lat2 = latLng2.lat * rad;
  const lon1 = latLng1.lng * rad;
  const lon2 = latLng2.lng * rad;
  const y = Math.sin(lon2 - lon1) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);

  const bearing = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  return bearing * 100; // the api returns headings in 100ths of a degree, so we replicate that functionality here
};

// deepCopy function derived from the lodash deep clone function taken from
// https://frontbackend.com/javascript/what-is-the-the-fastest-way-to-deep-clone-an-object-in-javascript
export const deepCopy = item => {
  if (!item) {
    return item;
  } // null, undefined values check

  const types = [Number, String, Boolean];
  let result;

  // normalizing primitives if someone did new String('aaa'), or new Number('444');
  types.forEach(type => {
    if (item instanceof type) {
      result = type(item);
    }
  });

  if (typeof result === 'undefined') {
    if (Object.prototype.toString.call(item) === '[object Array]') {
      result = [];
      item.forEach((child, index, array) => {
        result[index] = deepCopy(child);
      });
    } else if (typeof item === 'object') {
      // testing that this is DOM
      if (item.nodeType && typeof item.cloneNode === 'function') {
        result = item.cloneNode(true);
      } else if (!item.prototype) {
        // check that this is a literal
        if (item instanceof Date) {
          result = new Date(item);
        } else {
          // it is an object literal
          result = {};
          // tslint:disable-next-line:forin
          for (const i in item) {
            result[i] = deepCopy(item[i]);
          }
        }
      } else {
        result = item;
      }
    } else {
      result = item;
    }
  }

  return result;
};

export const isCancelledRequest = (response: HttpEvent<any>) => Boolean(response.type === 0);

export const arrayEquals = (a, b) => {
  return (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((val, index) => val === b[index.toString()])
  );
};

export const isLocalDevEnv = () => {
  // tests also run in environment = local and localhost as domain name
  // but tests are localhost:9876 instead of localhost:5000
  return (
    window?.location?.origin?.includes('localhost:5000') ||
    window?.location?.origin?.includes('local.dev') ||
    window?.location?.origin?.includes('maps.demo.vdo-fleet.com')
  );
};

export const isEUEnv = (envService: EnvironmentService): boolean => {
  return envService.getEnvironment()?.region === EUROPE;
};

export const extractIds = (items: Identifyable[]): string[] => {
  return items.map(item => item.id);
};

export function deduplicateArray<Type>(arr: Type[]): Type[] {
  return [...new Set(arr)];
}

export const intersection = (array1: any[], array2: any[]) => array1.filter(item => array2.includes(item));

export const isBoolean = val => {
  return typeof val === 'boolean';
};

export const getDriverName = (asset: ViewableAsset) => {
  const driverFirstName = asset?.driverFirstName;
  const driverLastName = asset?.driverLastName;
  if (driverFirstName && driverLastName) {
    return `${driverFirstName} ${driverLastName}`;
  }
  if (driverFirstName && !driverLastName) {
    return driverFirstName;
  }
  if (!driverFirstName && driverLastName) {
    return driverLastName;
  }
  return null;
};

export const getDriverName2 = (asset: ViewableAsset, driversMap: Map<string, DriverProfle>) => {
  if (asset.driverProfileId) {
    const driverProfile: DriverProfle = driversMap.get(asset.driverProfileId);
    if (driverProfile) {
      const driverFirstName = driverProfile?.legacyFirstName;
      const driverLastName = driverProfile?.legacyLastName;
      if (driverFirstName && driverLastName) {
        return `${driverFirstName} ${driverLastName}`;
      }
      if (driverFirstName && !driverLastName) {
        return driverFirstName;
      }
      if (!driverFirstName && driverLastName) {
        return driverLastName;
      }
    }
  }
  return null;
};

export const toTitleCase = (str: string): string => {
  return str.replace(/([^\W_]+[^\s-]*) */g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

export const discardEmptyValues = entity => {
  if (entity?.value) return entity;
};

export const patchAssetData = (
  asset: Asset,
  vehiclesMap: Map<string, VehicleListItem>,
  assetViewType: string
): Asset => {
  let patchedAsset = { ...asset };
  const vehicle = vehiclesMap.get(asset.assetId);
  if (vehicle) {
    patchedAsset.assetName = vehicle.label || vehicle.displayName || asset.assetId;
    patchedAsset.divisionId = vehicle.siteId;
    patchedAsset.assetDisplayName = assetViewType === 'VRN' ? vehicle.vrn : patchedAsset.assetName;
    // TODO we should remove this assetTypeInfo field
    patchedAsset.assetTypeInfo = {
      vin: vehicle.vin,
      licenseNumber: vehicle.vrn
    };
    patchedAsset.vin = vehicle.vin;
    patchedAsset.vrn = vehicle.vrn;
  } else {
    patchedAsset.assetName = asset.assetId; // set name to vin if no alternative
    patchedAsset.assetDisplayName = asset.assetId;
  }
  return patchedAsset;
};

export const patchAssetsData = (
  assets: Asset[],
  vehiclesMap: Map<string, VehicleListItem>,
  assetViewType: string
): Asset[] => {
  const resp = [];
  if (!assets?.length || vehiclesMap.size == 0) {
    return assets;
  }
  for (let asset of assets) {
    resp.push(patchAssetData(asset, vehiclesMap, assetViewType));
  }
  return resp;
};

// keep zonar structure for refacto
export const convertToDriverAsset = (
  source: ApiDriverAsset,
  driversMap: Map<string, DriverProfle>,
  vin: string
): DriverAsset => {
  const driverProfile: DriverProfle = driversMap.get(source.driverCardNumber);
  return {
    id: source.driverCardNumber + '_' + vin,
    driverProfileId: source.driverCardNumber,
    driverId: driverProfile?.driverId,
    assetId: vin,
    source: 'vdo',
    startTimestamp: source.startAssociation?.replace('Z', ''),
    endTimestamp: source.endAssociation?.replace('Z', ''),
    status: 'ACTIVE',
    created: '',
    modified: '',
    driverProfile: {
      ...driverProfile,
      status: 'ACTIVE',
      cdl: source.driverCardNumber,
      created: '',
      modified: '',
      cdlState: '',
      employeeNumber: '',
      tagId: 0,
      pin: 0
    }
  };
};

export const convertToDriverAssets = (
  source: ApiDriverAssetResponse,
  driversMap: Map<string, DriverProfle>
): DriverAsset[] => {
  const res = [];
  for (let apiDriverAsset of source.associations) {
    res.push(convertToDriverAsset(apiDriverAsset, driversMap, source.vin));
  }
  return res;
};
